"use client";

import BlokWrap from "@/components/bloks/BlokWrap";
import { ShoeFinderIntro } from "@/features/shoe-finder/ShoeFinderIntro";
import { ShoeFinderResults } from "@/features/shoe-finder/ShoeFinderResults";
import { ShoeFinderStep } from "@/features/shoe-finder/ShoeFinderStep";
import {
	buildSelectionSummary,
	computeMaxVisibleSteps,
	computeVisibleSteps,
} from "@/features/shoe-finder/filters";
import {
	shoeFinderSelectionsAtom,
	shoeFinderStartFiredAtom,
	shoeFinderStepAtom,
} from "@/features/shoe-finder/state";
import { trackShoeFinderStart } from "@/features/shoe-finder/tracking";
import { useTranslations } from "@/i18n/client";
import type { BlokShoeFinder } from "@/storyblok";
import type { BlokProps } from "@frend-digital/storyblok";
import { useAtom, useAtomValue } from "jotai";
import { useEffect, useMemo, useRef } from "react";
import styles from "./ShoeFinderBlok.module.css";

export const ShoeFinderBlok = ({ blok }: BlokProps<BlokShoeFinder>) => {
	const t = useTranslations();
	const [step, setStep] = useAtom(shoeFinderStepAtom);
	const selections = useAtomValue(shoeFinderSelectionsAtom);
	const [startFired, setStartFired] = useAtom(shoeFinderStartFiredAtom);
	const rootRef = useRef<HTMLDivElement>(null);
	const prevStepRef = useRef(step);

	useEffect(() => {
		if (startFired) return;
		if (selections.size === 0) return;
		trackShoeFinderStart();
		setStartFired(true);
	}, [selections, startFired, setStartFired]);

	const visibleSteps = useMemo(
		() => computeVisibleSteps(blok.steps ?? [], selections),
		[blok.steps, selections],
	);
	const stepsCount = visibleSteps.length;
	const maxStepsCount = useMemo(
		() => computeMaxVisibleSteps(blok.steps ?? [], selections, step),
		[blok.steps, selections, step],
	);
	const showResults = step >= stepsCount;
	const sizeLabel = t("Size");
	const selectionSummary = useMemo(
		() => buildSelectionSummary(visibleSteps, selections, step, sizeLabel),
		[visibleSteps, selections, step, sizeLabel],
	);

	useEffect(() => {
		if (prevStepRef.current === step) return;
		prevStepRef.current = step;
		rootRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
	}, [step]);

	if (step === -1) {
		return (
			<BlokWrap editable={blok}>
				<ShoeFinderIntro blok={blok} />
			</BlokWrap>
		);
	}

	const progressPercent = maxStepsCount > 0 ? (step / maxStepsCount) * 100 : 0;

	const prevStepTitle = step > 0 ? visibleSteps[step - 1]?.title : null;
	const currentStep = !showResults ? visibleSteps[step] : null;
	const backgroundImage = currentStep?.backgroundImage;

	return (
		<div ref={rootRef} className={styles.scrollTarget}>
			{backgroundImage?.filename && (
				<img
					className={styles.backgroundImage}
					src={backgroundImage.filename}
					alt={backgroundImage.alt ?? ""}
					aria-hidden="true"
				/>
			)}
			<BlokWrap editable={blok} className={styles.root}>
				<div className={styles.titleBlock}>
					<h1 className={styles.pageTitle}>{blok.introTitle}</h1>
					{step === 0 ? (
						blok.introDescription && (
							<p className={styles.pageDescription}>{blok.introDescription}</p>
						)
					) : (
						<>
							{selectionSummary && !showResults && (
								<p className={styles.selectionSummary}>
									<strong>{blok.resultsShowingLabel || "Showing results for:"} </strong>
									{selectionSummary}
								</p>
							)}
							<button type="button" className={styles.backButton} onClick={() => setStep(step - 1)}>
								<svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
									<path
										d="M14 7L9 12L14 17"
										stroke="currentColor"
										strokeWidth="1.5"
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
								</svg>
								<span>{prevStepTitle}</span>
							</button>
						</>
					)}
				</div>
				{!showResults && step > 0 && (
					<div className={styles.progressWrap}>
						<span className={styles.progressCount}>
							{step}/{maxStepsCount}
						</span>
						<div className={styles.progressBar}>
							<div className={styles.progressTrack} />
							<div className={styles.progressFill} style={{ width: `${progressPercent}%` }} />
						</div>
					</div>
				)}
				{showResults ? (
					<ShoeFinderResults blok={blok} visibleSteps={visibleSteps} />
				) : (
					<ShoeFinderStep blok={blok} visibleSteps={visibleSteps} />
				)}
			</BlokWrap>
		</div>
	);
};
